import debounce from 'lodash/debounce';
import { API } from '../../types';
import ajaxRequest from '../../helpers/ajaxRequest';
import getPressedKey, { KeyboardMap } from '../../helpers/getPressedKey';
import { handleEnterManualAddress } from './addressForm';
import { invalidFormFieldLabelPositionHandler, clearElement, HIDE_CLASS } from './utils';

const PL_SEARCH_ERROR_NO_RESULTS = 'No addresses were found.';

interface AddressData {
  Error?: string | null;
  Address: {
    Line1: string;
    Line2: string;
    PostTown: string;
    Postcode: string;
    County: string;
    Country: string;
  };
}

export default function initPostcodeHandler(): void {
  let query: string | undefined = '';
  initPostcodeLookupEvents();

  const postcodeInput = document.querySelector<HTMLInputElement>('.postcode-input');

  postcodeInput?.addEventListener(
    'keyup',
    debounce(() => {
      const searchQuery = getPostcodeLookupSearchValue() || '';

      if (searchQuery === query) {
        return;
      }

      query = searchQuery;

      document.querySelector<HTMLElement>('.postcode .preloader')?.classList.remove(HIDE_CLASS);
      document.querySelector<HTMLElement>('.js-select-error .js-select-address')?.classList.add(HIDE_CLASS);
      const postcodeErrorsContainer = document.getElementById('postCodeErrors');
      clearElement(postcodeErrorsContainer);
      getPostcodeLookupAddresses(query);
    }, 200)
  );
}

function initPostcodeLookupEvents(): void {
  const postcodeInput = document.querySelector<HTMLInputElement>('.postcode-input');

  postcodeInput?.addEventListener('keypress', (e) => {
    const { which } = e;
    const { keyCode } = e;
    if ((which && which === 13) || (keyCode && keyCode === 13)) {
      e.stopPropagation();
      e.preventDefault();
      postcodeInput.blur();
      return false;
    }
    return true;
  });

  postcodeInput?.addEventListener('keydown', (e) => {
    const { which } = e;
    const { keyCode } = e;
    if ((which && which === 40) || (keyCode && keyCode === 40)) {
      document.querySelector<HTMLElement>('.address-picker__list li:first-child')?.focus();
    }
  });
}

function getPostcodeLookupSearchValue(): string | undefined {
  return document.querySelector<HTMLInputElement>('.postcode-input')?.value.replace(/[^A-Za-z0-9 ]/g, '');
}

function fetchPostcodeLookupAddresses(query: string): Promise<[]> {
  return ajaxRequest({
    url: API.ADDRESS_LOOKUP,
    method: 'GET',
    data: {
      query,
      id: Math.random().toString(36).substring(9)
    }
  });
}

function postcodeLookupErrorHandler(): void {
  document.querySelector<HTMLElement>('.postcode .preloader')?.classList.add(HIDE_CLASS);
  const errorsContainer = document.getElementById('postCodeErrors');

  if (errorsContainer) {
    errorsContainer.innerText = PL_SEARCH_ERROR_NO_RESULTS;
  }

  document.querySelector<HTMLElement>('.js-edit-address-manually .js-select-error')?.classList.remove(HIDE_CLASS);
}

function postcodeLookupAddressSelect(): void {
  const selectAddress = document.querySelector<HTMLElement>('.js-select-address ul');
  const clickHandler = (e: Event): void => {
    const target = e.target as HTMLElement;
    const liEl = target.nodeName.toLowerCase() === 'li' ? target : target.closest<HTMLElement>('li');
    if (liEl) {
      const { address, type } = target.dataset;
      const form = document.getElementById('addressForm');
      populatePostcodeLookupAddress(form, type, JSON.parse(address as string));
    }
  };

  selectAddress?.removeEventListener('click', clickHandler);
  selectAddress?.addEventListener('click', clickHandler);

  const keyDownHandler = (e: KeyboardEvent): void => {
    const pressedKey = getPressedKey(e);
    const targetEl = e.target as HTMLElement;

    if (KeyboardMap.ARROW_DOWN === pressedKey) {
      e.preventDefault();
      if (targetEl.parentElement !== selectAddress || !targetEl.nextSibling) {
        (selectAddress?.firstChild as HTMLElement)?.focus();
      } else {
        (targetEl.nextSibling as HTMLElement)?.focus();
      }
    } else if (KeyboardMap.ARROW_UP === pressedKey) {
      e.preventDefault();
      if (targetEl.parentElement !== selectAddress || !targetEl.previousSibling) {
        (selectAddress?.lastChild as HTMLElement)?.focus();
      } else {
        (targetEl.previousSibling as HTMLElement)?.focus();
      }
    } else if (KeyboardMap.ENTER === pressedKey) {
      e.preventDefault();
      clickHandler(e);
      document.removeEventListener('keydown', keyDownHandler);
      document.getElementById('address.postcodeLookup')?.removeEventListener('keydown', keyDownHandler);
    } else if (KeyboardMap.ESCAPE === pressedKey) {
      e.preventDefault();
      document.querySelector<HTMLInputElement>('.postcode-input')?.focus();
    }
  };
  selectAddress?.focus();
  selectAddress?.removeEventListener('keydown', keyDownHandler);
  selectAddress?.addEventListener('keydown', keyDownHandler);
  document.getElementById('address.postcodeLookup')?.removeEventListener('keydown', keyDownHandler);
  document.getElementById('address.postcodeLookup')?.addEventListener('keydown', keyDownHandler);
}

function getPostcodeLookupAddresses(searchQuery: string): void {
  fetchPostcodeLookupAddresses(searchQuery)
    .then((addresses: AddressData[]) => {
      const addrListWrapper = document.querySelector<HTMLElement>('.js-select-address');
      const addrList = addrListWrapper?.querySelector<HTMLElement>('ul');
      const postCodeErrors = document.querySelector<HTMLElement>('.postcode-search #postCodeErrors');
      const preloader = document.querySelector<HTMLElement>('.postcode .preloader');
      const errorMessage = addresses[0] ? addresses[0].Error : '';

      clearElement(addrList);

      if (errorMessage) {
        postcodeLookupErrorHandler();
        addrListWrapper?.classList.add(HIDE_CLASS);
      } else {
        clearElement(postCodeErrors);
        document.querySelector<HTMLElement>('.js-select-error')?.classList.add(HIDE_CLASS);
        addrListWrapper?.classList.add(HIDE_CLASS);
        preloader?.classList.add(HIDE_CLASS);

        if (!addresses.length) {
          postcodeLookupErrorHandler();
        }

        if (addresses.length > 0) {
          addresses.forEach((addressData: AddressData) => {
            const { Line1, Line2, PostTown, Postcode, County, Country } = addressData.Address;
            const formattedAddress = [Line1, Line2, PostTown, Postcode, County, Country].filter(Boolean).join(', ');
            const newNode = document.createElement<'li'>('li');
            newNode.setAttribute('tabindex', '0');
            newNode.innerText = formattedAddress;
            newNode.dataset.type = 'address';
            newNode.dataset.address = JSON.stringify(addressData.Address);
            addrList?.appendChild(newNode);
          });
          addrListWrapper?.classList.remove(HIDE_CLASS);
        }
      }
      postcodeLookupAddressSelect();
    })
    .catch(postcodeLookupErrorHandler);
}

function populatePostcodeLookupAddress(form, type, address): void {
  const selectParent = document.querySelector<HTMLElement>('.js-select-address');

  (document.getElementById(`${type}.line1`) as HTMLInputElement).value = address.Line1;
  (document.getElementById(`${type}.line2`) as HTMLInputElement).value = address.Line2;
  (document.getElementById(`${type}.townCity`) as HTMLInputElement).value = address.PostTown;
  (document.getElementById(`${type}.county`) as HTMLInputElement).value = address.County;
  (document.getElementById(`${type}.postcode`) as HTMLInputElement).value = address.Postcode;
  (document.getElementById(`${type}.country`) as HTMLSelectElement).value = 'GB';
  (document.getElementById(`${type}.country`) as HTMLSelectElement).dispatchEvent(new CustomEvent('change'));

  form.querySelectorAll(`.manual-address .text`).forEach((input) => {
    input.dispatchEvent(new CustomEvent('keyup'));
    input.dispatchEvent(new CustomEvent('change'));
  });

  invalidFormFieldLabelPositionHandler(`#${form.id}`);

  if (selectParent) {
    selectParent.classList.add(HIDE_CLASS);
    handleEnterManualAddress(selectParent);
  }
}
